<template>
  <div class="production-allocation-chart">
    <StackedBarChart
      :chartData="currentChartData"
      :height="300"
      :yAxisTitle="$t('common.mwh')"
      :showLegend="false"
    />

    <AllocationLegend
      v-if="!chartViewMode || chartViewMode === 'allocations'"
      :allocations="allocations"
    />

    <!-- Company legend for company view mode -->
    <div
      v-else-if="chartViewMode === 'companies'"
      class="company-legend-wrapper"
    >
      <div
        class="company-legend"
        :class="{ 'is-collapsed': isLegendCollapsed }"
      >
        <div class="row g-2">
          <div
            v-for="legendItem in visibleLegendItems"
            :key="legendItem.company.id"
            class="col-12 col-sm-6 col-md-3"
          >
            <div
              class="legend-item d-flex align-items-center"
              :class="{
                'legend-item-clickable': true,
                'legend-item-hidden': hiddenCompanyIds.has(
                  legendItem.company.id!
                ),
              }"
              @click="toggleCompanyVisibility(legendItem.company.id!)"
              role="button"
              tabindex="0"
              @keydown.enter="toggleCompanyVisibility(legendItem.company.id!)"
              @keydown.space.prevent="
                toggleCompanyVisibility(legendItem.company.id!)
              "
            >
              <span
                class="legend-line me-2"
                :style="{ backgroundColor: legendItem.color }"
              ></span>
              <div class="legend-text">
                <div class="legend-label text-muted small">
                  {{ legendItem.company.name }}
                </div>
                <div class="legend-value fw-semibold">
                  {{ formatMwh(legendItem.production) }}
                  {{ $t("common.mwh") }}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-if="isLegendCollapsed" class="legend-fade-overlay"></div>
      </div>
      <div v-if="shouldShowExpandButton" class="text-center mt-3">
        <button
          v-if="isLegendCollapsed"
          type="button"
          class="btn btn-sm btn-outline-secondary"
          @click="expandLegend"
        >
          <FontAwesomeIcon icon="chevron-down" class="me-2" />
          {{ $t("production_strategy.show_all_companies") }}
          ({{ sortedLegendItems.length }})
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import StackedBarChart from "@/common/StackedBarChart.vue";
import { getCompanyColor } from "@/composables/colorUtils";
import { formatMwh } from "@/composables/formatUtils";
import type { Company } from "@/types";
import type { StackableDataset } from "@/types/ChartData";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { computed, ref } from "vue";
import AllocationLegend from "./AllocationLegend.vue";
import type {
  AllocationPlanResult,
  AllocationData,
  ChartData,
} from "./useProductionAllocationData";

const props = defineProps<{
  chartData: ChartData;
  allocations: AllocationData;
  chartViewMode?: "allocations" | "companies";
  companies?: Company[];
  companyResults?: AllocationPlanResult[];
}>();

// State for legend expansion and company visibility
const isLegendCollapsed = ref(true);
const hiddenCompanyIds = ref<Set<number>>(new Set());

// Sorted legend items by production volume (descending)
interface LegendItem {
  company: Company;
  production: number;
  originalIndex: number;
  color: string;
}

const sortedLegendItems = computed<LegendItem[]>(() => {
  if (!props.companies || !props.companyResults) return [];

  return props.companies
    .map((company, index) => ({
      company,
      production: getCompanyProduction(index),
      originalIndex: index,
      color: getCompanyColor(index),
    }))
    .sort((a, b) => b.production - a.production);
});

// Determine how many items to show when collapsed (2 rows = 8 items in 4-column grid)
const COLLAPSED_ROW_COUNT = 2;
const ITEMS_PER_ROW = 4;
const COLLAPSED_ITEM_COUNT = COLLAPSED_ROW_COUNT * ITEMS_PER_ROW;

const shouldShowExpandButton = computed(() => {
  return sortedLegendItems.value.length > COLLAPSED_ITEM_COUNT;
});

const visibleLegendItems = computed(() => {
  if (!isLegendCollapsed.value || !shouldShowExpandButton.value) {
    return sortedLegendItems.value;
  }
  return sortedLegendItems.value.slice(0, COLLAPSED_ITEM_COUNT);
});

// Toggle company visibility in chart
function toggleCompanyVisibility(companyId: number) {
  const newSet = new Set(hiddenCompanyIds.value);
  if (newSet.has(companyId)) {
    newSet.delete(companyId);
  } else {
    newSet.add(companyId);
  }
  hiddenCompanyIds.value = newSet;
}

// Expand legend
function expandLegend() {
  isLegendCollapsed.value = false;
}

// Current chart data based on view mode
const currentChartData = computed(() => {
  if (props.chartViewMode === "companies" && props.companyResults) {
    return buildCompanyProductionChartData();
  }
  return stackedChartData.value;
});

// Transform ChartData to StackedBarChart format (allocation view)
const stackedChartData = computed(() => {
  return {
    labels: props.chartData.labels,
    datasets: props.chartData.datasets.map((dataset): StackableDataset => ({
      ...dataset,
      borderWidth: 0, // Add required borderWidth property
    })),
  };
});

// Build company production chart data with hidden companies filtered
function buildCompanyProductionChartData() {
  if (!props.companyResults || !props.companies) {
    return { labels: [], datasets: [] };
  }

  const labels = props.chartData.labels;

  const datasets = props.companies
    .map((company, index) => {
      // Skip hidden companies
      if (company.id && hiddenCompanyIds.value.has(company.id)) {
        return null;
      }

      const companyResult = props.companyResults?.[index];
      const productionByMonth = new Map(
        (companyResult?.perMonth ?? []).map((month) => [
          `${month.year}-${month.monthIndex}`,
          month.production,
        ])
      );
      const data = props.chartData.months.map(
        ({ monthIndex, year }) =>
          productionByMonth.get(`${year}-${monthIndex}`) ?? 0
      );

      return {
        label: company.name,
        data,
        backgroundColor: getCompanyColor(index),
        borderColor: getCompanyColor(index),
        borderWidth: 0,
      };
    })
    .filter((dataset): dataset is StackableDataset => dataset !== null);

  return { labels, datasets };
}

function getCompanyProduction(companyIndex: number): number {
  if (!props.companyResults?.[companyIndex]) return 0;

  const companyResult = props.companyResults[companyIndex];
  return companyResult.totals?.produced || 0;
}
</script>
<style scoped lang="scss">
.production-allocation-chart {
  position: relative;
  width: 100%;

  /* Legend handled by AllocationLegend component */
}

// Company legend styles
.company-legend-wrapper {
  margin-top: 1.5rem;
}

.company-legend {
  position: relative;
  transition: max-height 0.3s ease;

  &.is-collapsed {
    max-height: 180px;
    overflow: hidden;
  }

  .legend-item {
    padding: 0.5rem;
    transition:
      opacity 0.2s ease,
      transform 0.2s ease;
  }

  .legend-item-clickable {
    cursor: pointer;
    border-radius: 0.375rem;

    &:hover {
      background-color: rgba(var(--phoenix-primary-rgb), 0.05);
      transform: translateX(2px);
    }

    &:focus {
      outline: 2px solid var(--phoenix-primary);
      outline-offset: 2px;
    }
  }

  .legend-item-hidden {
    opacity: 0.35;

    .legend-line {
      opacity: 0.5;
    }

    .legend-text {
      text-decoration: line-through;
    }
  }

  .legend-line {
    width: 4px;
    height: 32px;
    border-radius: 1px;
    flex-shrink: 0;
    transition: opacity 0.2s ease;
  }

  .legend-text {
    flex: 1;
    min-width: 0;
    transition: text-decoration 0.2s ease;
  }

  .legend-label {
    font-size: 0.875rem;
    line-height: 1.2;
  }

  .legend-value {
    font-size: 0.9375rem;
  }
}

.legend-fade-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 80px;
  background: linear-gradient(
    to bottom,
    transparent 0%,
    rgba(255, 255, 255, 0.8) 50%,
    rgba(255, 255, 255, 0.95) 100%
  );
  pointer-events: none;
}
</style>
